import { Close, Delete, Edit, EditNote, Save } from '@mui/icons-material';
import { CardProps } from '../general/Card';
import React from 'react';
import WhiteFab from '../common/WhiteFab';
import { Stack, TextField, Tooltip } from '@mui/material';
import NumericFormatInput from '../general/NumericFormatInput';
import { Button } from 'components/index';
import { TOOLTIPS, parseDynamicDataString } from './report.util';
import SimpleCard from '../general/SimpleCard';
import { Typography } from 'components/index';
import { format } from 'common/utils/common.util';
import { NEGATIVE_NUMBER_COLOR } from 'common/constants/general';
import { ReportStatusEnum } from './report.type';

type DynamicDataCardProps = {
  dynamicDataString: string;
  updateDynamicData: (updatedString: string) => void;
  isLocked?: boolean;
  reportStatus: string;
  cardProps?: Partial<CardProps>;
};

const DynamicDataCard: React.FC<DynamicDataCardProps> = ({
  dynamicDataString,
  updateDynamicData,
  isLocked = false,
  reportStatus,
  cardProps,
}) => {
  const [isEditMode, setIsEditMode] = React.useState(false);
  const toggleEditMode = () => setIsEditMode(val => !val);

  const [dynamicDataObj, setDynamicDataObj] = React.useState(parseDynamicDataString(dynamicDataString));

  const [newField, setNewField] = React.useState('');
  const [newFieldValue, setNewFieldValue] = React.useState('');

  const [fieldToEdit, setFieldToEdit] = React.useState('');
  const [editFieldValue, setEditFieldValue] = React.useState('');

  const noDataToShow = Object.keys(dynamicDataObj).length === 0;

  // reset if props change
  React.useEffect(() => {
    if (isEditMode) setIsEditMode(false);
  }, [dynamicDataString, isLocked]);

  const handleCancel = () => {
    setIsEditMode(false);
    setFieldToEdit('');
    setDynamicDataObj(parseDynamicDataString(dynamicDataString));
  };

  const handleSaveDraft = () => {
    const updatedData = JSON.stringify(dynamicDataObj);
    updateDynamicData(updatedData);
    setIsEditMode(false);
  };

  const startEditingField = (key: string) => {
    setFieldToEdit(key);
    setEditFieldValue(dynamicDataObj[key]);
  };

  const handleSaveEditedField = () => {
    const updatedData = { ...dynamicDataObj };
    updatedData[fieldToEdit] = editFieldValue;
    setDynamicDataObj(updatedData);
    setFieldToEdit('');
    setEditFieldValue('');
  };

  const handleCancelEditedField = () => {
    setFieldToEdit('');
    setEditFieldValue('');
  };

  const removeField = (key: string) => {
    const updatedData = { ...dynamicDataObj };
    delete updatedData[key];
    setDynamicDataObj(updatedData);
  };

  const newFieldIsUnique = React.useMemo(() => {
    const existingFields = Object.keys(dynamicDataObj).map(field => field.toLowerCase());
    return !existingFields.includes(newField.toLowerCase());
  }, [newField, dynamicDataObj]);

  const canAddNewField = React.useMemo(() => {
    return newField.trim() && newFieldValue && newFieldIsUnique;
  }, [newField, newFieldValue, dynamicDataObj]);

  const addNewField = () => {
    if (canAddNewField) {
      const updatedData = {
        ...dynamicDataObj,
        [newField]: newFieldValue,
      };

      setDynamicDataObj(updatedData);
      setNewField('');
      setNewFieldValue('');
    }
  };

  return (
    <form style={{ height: '100%' }}>
      <SimpleCard
        icon={cardProps?.icon ?? <EditNote color='primary' />}
        heading={cardProps?.heading ?? 'Others'}
        headerAction={
          isEditMode ? (
            <WhiteFab aria-label='cancel' icon={<Close fontSize='small' />} textColor='error' size='small' onClick={handleCancel} />
          ) : (
            <Tooltip
              title={
                isLocked && reportStatus === ReportStatusEnum.Completed
                  ? TOOLTIPS.statusCompleted
                  : isLocked
                    ? TOOLTIPS.unlockToEdit
                    : 'Click to add a new field or edit existing ones.'
              }
              arrow={false}
            >
              <span>
                <WhiteFab
                  aria-label='edit'
                  icon={<Edit fontSize='small' />}
                  textColor='primary'
                  size='small'
                  disabled={isLocked}
                  onClick={toggleEditMode}
                />
              </span>
            </Tooltip>
          )
        }
        footer={
          isEditMode ? (
            <Button onClick={handleSaveDraft} sx={{ marginLeft: 'auto' }}>
              Save Draft
            </Button>
          ) : undefined
        }
        alignItems='left'
      >
        <Stack spacing={2} mb={1}>
          {noDataToShow && !isEditMode && (
            <Typography variant='caption' fontWeight='medium'>
              Click the edit icon to add a new field.
            </Typography>
          )}
          {Object.keys(dynamicDataObj).map(key => (
            <Stack key={key} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
              <Stack>
                <Typography variant='caption' color='text' fontWeight='medium' textTransform='capitalize'>
                  {key}:
                </Typography>
                {key == fieldToEdit ? (
                  <TextField
                    name='editFieldValue'
                    value={editFieldValue}
                    placeholder='Enter a number'
                    onChange={e => setEditFieldValue(e.target.value)}
                    fullWidth
                    variant='standard'
                    InputProps={{
                      inputComponent: NumericFormatInput as any,
                    }}
                  />
                ) : (
                  <Typography
                    variant='body2'
                    fontWeight='regular'
                    sx={{
                      whiteSpace: 'pre-wrap',
                      textWrap: 'wrap',
                      overflowWrap: 'anywhere',
                      marginRight: 1,
                      color: dynamicDataObj[key] < 0 ? NEGATIVE_NUMBER_COLOR : undefined,
                    }}
                  >
                    {format(dynamicDataObj[key] ?? '--', 'number')}
                  </Typography>
                )}
              </Stack>
              <Stack direction={'row'} spacing={1}>
                {isEditMode ? (
                  key == fieldToEdit ? (
                    <React.Fragment key={key}>
                      <WhiteFab
                        aria-label='save'
                        icon={<Save fontSize='small' />}
                        textColor='primary'
                        size='small'
                        onClick={handleSaveEditedField}
                      />
                      <WhiteFab
                        aria-label='cancel'
                        icon={<Close fontSize='small' />}
                        textColor='error'
                        size='small'
                        onClick={handleCancelEditedField}
                      />
                    </React.Fragment>
                  ) : (
                    <React.Fragment key={key}>
                      <WhiteFab
                        aria-label='edit'
                        icon={<Edit fontSize='small' />}
                        textColor='primary'
                        size='small'
                        onClick={() => startEditingField(key)}
                      />
                      <WhiteFab
                        aria-label='delete'
                        icon={<Delete fontSize='small' />}
                        textColor='error'
                        size='small'
                        onClick={() => removeField(key)}
                      />
                    </React.Fragment>
                  )
                ) : null}
              </Stack>
            </Stack>
          ))}

          {isEditMode && (
            <Stack direction={'row'} spacing={2} justifyContent={'space-between'} alignItems={'flex-end'}>
              <Stack spacing={0.5} flex={1}>
                <TextField
                  label='Title'
                  name='newField'
                  value={newField}
                  onChange={e => setNewField(e.target.value)}
                  error={!newFieldIsUnique}
                  helperText={newFieldIsUnique ? undefined : 'Field name should be unique'}
                  variant='standard'
                />
                <TextField
                  label='Value'
                  placeholder='Enter a number'
                  name='newFieldValue'
                  value={newFieldValue}
                  onChange={e => setNewFieldValue(e.target.value)}
                  variant='standard'
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                  }}
                />
              </Stack>
              <Stack>
                <Button onClick={addNewField} disabled={!canAddNewField}>
                  Add
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </SimpleCard>
    </form>
  );
};

export default DynamicDataCard;
